// @flow
import * as React from 'react';
import { translate } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import RefreshToken from 'common/modules/session/domain/RefreshToken';
import BackgroundPage from 'common/components/BackgroundPage';

import Images from '../../themes/Images';
import SignUpForm from './components/SignUpForm';
import FolloSignUpAPI from './services/FolloSignUpAPI';
import { loginFromToken } from '../authentication/actions/authenticationActions';
import type AppState from '../../redux/types/AppState';
import type { FolloUserProfile } from './domain/FolloUserProfile';
import FolloSignupWithStatsRadioDialog from './components/FolloSignupWithStatsRadioDialog';
import CampaignAlreadyLinkedError from './domain/CampaignAlreadyLinkedError';
import PropositionAlreadyLinkedError from './domain/PropositionAlreadyLinkedError';

export const SIGN_UP_FORM_ID = 'sign-up-form';


interface Props {
  t: any;
  match: any;
  session: Object;

  actions: {
    loginFromToken: (refreshToken: RefreshToken) => void,
    openSnackbar: (string, string) => void,
  }
}

interface State {
  displayNoNewAccountCreated: boolean;
}

export class SignUpPage extends React.Component<Props, State> {

  state = { displayNoNewAccountCreated: false };

  isValidSignUpMethod = () => {
    const { signUpWith } = this.props.match.params;
    return signUpWith === 'campaign' || signUpWith === 'proposition';
  };

  createRedirectionPathName = () => {
    const { signUpWith, reference } = this.props.match.params;

    switch (signUpWith) {
      case 'campaign':
        return `/campaigns/purchased/${reference}`;
      case 'proposition':
        return `/campaigns/proposed/${reference}`;
      default:
        throw `Cannot sign up with ${signUpWith}`;
    }
  };

  createSignupFunction = () => {
    const { signUpWith } = this.props.match.params;

    switch (signUpWith) {
      case 'campaign':
        return FolloSignUpAPI.signUpWithCampaign;
      case 'proposition':
        return FolloSignUpAPI.signUpWithProposition;
      default:
        throw `Cannot sign up with ${signUpWith}`;
    }
  };

  onSignUp = async (id: string, profile: FolloUserProfile) => {
    const { t, actions } = this.props;
    const signUp = this.createSignupFunction();

    try {
      const login = await signUp(id, profile);

      const refreshToken = new RefreshToken(login.refreshToken, login.expiration);
      const displayNoNewAccountCreated = !login.isNewAccount;

      this.setState({ displayNoNewAccountCreated }, () => actions.loginFromToken(refreshToken));
    } catch (e) {
      if (e instanceof CampaignAlreadyLinkedError) {
        actions.openSnackbar('error', t('campaignAlreadyLinked'));
      } else if (e instanceof PropositionAlreadyLinkedError) {
        actions.openSnackbar('error', t('propositionAlreadyLinked'));
      } else {
        actions.openSnackbar('error', t('cantSignUp'));
      }
    }
  };

  onCloseNoNewAccountCreated = () => {
    this.setState({ displayNoNewAccountCreated: false });
  };

  render() {
    const { t, match, session } = this.props;
    const { displayNoNewAccountCreated } = this.state;
    const { reference } = match.params;

    if (!this.isValidSignUpMethod()) {
      return <Redirect to="/login" />;
    }

    if (!displayNoNewAccountCreated && session.isAuthenticated) {
      const path = this.createRedirectionPathName();
      return <Redirect to={path} />;
    }

    return (
      <BackgroundPage background={Images.background} title={t('signup')} logo={Images.logo}>
        <SignUpForm campaignReference={reference} onSignUp={this.onSignUp} id={SIGN_UP_FORM_ID} />
        <FolloSignupWithStatsRadioDialog
          open={displayNoNewAccountCreated}
          onClose={this.onCloseNoNewAccountCreated}
        />
      </BackgroundPage>
    );
  }
}

function mapStateToProps(state: AppState) {
  return {
    session: state.session,
  };
}

function mapDispatchToProps(dispatch: *) {
  return {
    actions: bindActionCreators({ loginFromToken, openSnackbar }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(translate('signup')(SignUpPage));



// WEBPACK FOOTER //
// ./src/advertisers/modules/signup/SignUpPage.js